import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map"]
  static values = {
    apiKey: String,
    markers: Array,
    mapId: String,
    url: String,
    zoomOnMarkers: Boolean
  }

  connect() {
    if (typeof google === "undefined") {
      if (window.googleMapsLoading) {
        window.addEventListener("google-maps-loaded", () => this.initMap())
      } else {
        this.loadGoogleMaps()
      }
    } else {
      this.initMap()
    }
  }

  loadGoogleMaps() {
    if (window.googleMapsLoading) return
    window.googleMapsLoading = true

    // Carrega o MarkerClusterer primeiro
    const clustererScript = document.createElement("script")
    clustererScript.src = "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js"
    clustererScript.async = true
    clustererScript.onload = () => {
      console.log("MarkerClusterer loaded")
      this.loadGoogleMapsScript()
    }
    document.head.appendChild(clustererScript)
  }

  loadGoogleMapsScript() {
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&callback=initMapInstance&v=weekly&loading=async&libraries=marker`
    script.async = true
    script.defer = true
    
    window.initMapInstance = () => {
      console.log("Google Maps API loaded")
      this.initMap()
      window.dispatchEvent(new Event("google-maps-loaded"))
    }
    document.head.appendChild(script)
  }

  async initMap() {
    // AdvancedMarkerElement requires a Map ID. 
    // If you don't have one, you can use 'DEMO_MAP_ID'.
    const mapOptions = {
      center: { lat: -15.7801, lng: -47.9292 }, // Centro do Brasil (Brasília)
      zoom: 4,
      mapId: this.mapIdValue || "DEMO_MAP_ID", 
    }

    const { Map } = await google.maps.importLibrary("maps")
    this.map = new Map(this.mapTarget, mapOptions)
    
    if (this.hasUrlValue) {
      this.loadMarkersFromUrl()
    } else {
      this.addMarkers()
    }
  }

  async loadMarkersFromUrl() {
    try {
      const response = await fetch(this.urlValue)
      const markersData = await response.json()
      this.addMarkers(markersData)
      
      // Se não houver pontos, talvez queiramos manter o zoom inicial ou centralizar
      if (markersData.length === 0) {
        console.log("Nenhum médico encontrado com coordenadas.")
      }
    } catch (error) {
      console.error("Erro ao carregar marcadores:", error)
    }
  }

  async addMarkers(markersToRender = null) {
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker")
    const { InfoWindow } = await google.maps.importLibrary("maps")
    const bounds = new google.maps.LatLngBounds()
    let hasMarkers = false
    const markers = []

    const markersData = markersToRender || this.markersValue

    markersData.forEach(markerData => {
      if (markerData.lat && markerData.lng) {
        const position = { lat: parseFloat(markerData.lat), lng: parseFloat(markerData.lng) }
        
        const pinOptions = {
          glyphColor: "white",
          background: "#4285F4",
          borderColor: "#3074d9",
        }

        if (markerData.doctors_count !== undefined) {
          pinOptions.glyphText = markerData.doctors_count.toString()
        }

        const pin = new PinElement(pinOptions)

        const marker = new AdvancedMarkerElement({
          position: position,
          title: markerData.name,
          content: pin.element,
        })

        const content = markerData.info_window || `<div><strong>${markerData.name}</strong>${markerData.state ? ` - ${markerData.state}` : ''}<br>Médicos: ${markerData.doctors_count || 1}</div>`

        const infoWindow = new InfoWindow({
          content: content
        })

        marker.addListener("click", () => {
          infoWindow.open(this.map, marker)
        })

        bounds.extend(position)
        markers.push(marker)
        hasMarkers = true
      }
    })

    if (hasMarkers) {
      if (this.zoomOnMarkersValue || (this.hasUrlValue && markersData.length > 0)) {
        this.map.fitBounds(bounds)
      }
      
      if (typeof markerClusterer !== "undefined") {
        new markerClusterer.MarkerClusterer({
          map: this.map,
          markers: markers
        })
      } else {
        markers.forEach(m => m.setMap(this.map))
      }
    }
  }
}
